import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

export default function AdminLayout() {
  const { user, bootstrapped, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  if (!bootstrapped) return <div className="min-h-screen bg-bg" />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const links = [
    { to: "/admin", label: t("admin.dashboard"), end: true },
    { to: "/admin/projects", label: t("admin.projects") },
    { to: "/admin/media", label: t("admin.media") },
    { to: "/admin/bookings", label: t("admin.bookings") },
    { to: "/admin/messages", label: t("admin.messages") },
    { to: "/admin/transactions", label: t("admin.transactions") },
    { to: "/admin/settings", label: t("admin.settings") },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-bg text-ink">
      <header className="border-b border-edge bg-bg-surface sticky top-0 z-30">
        <div className="px-4 sm:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-3 sm:gap-6 min-w-0">
            <span className="font-display text-lg sm:text-xl text-ink truncate">C—Carpenter</span>
            <span className="text-eyebrow text-brand hidden sm:inline">{t("admin.dashboard")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-eyebrow text-ink-muted hidden md:inline truncate max-w-[200px]">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                navigate("/admin/login");
              }}
              className="text-eyebrow text-ink-muted hover:text-brand transition-colors"
            >
              {t("admin.signOut")}
            </button>
          </div>
        </div>

        {/* Mobile: horizontal scrollable nav */}
        <nav className="md:hidden border-t border-edge overflow-x-auto no-scrollbar">
          <div className="flex gap-1 px-3 py-2 whitespace-nowrap">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-${l.to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-xs rounded-none ${
                    isActive
                      ? "bg-bg-hover text-brand border-b-2 border-brand"
                      : "text-ink-muted hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <div className="grid grid-cols-12">
        {/* Desktop sidebar */}
        <aside className="hidden md:block md:col-span-3 lg:col-span-2 border-r border-edge bg-bg-surface min-h-[calc(100vh-4rem)] p-4">
          <nav className="flex md:flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-desktop-${l.to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm ${
                    isActive
                      ? "bg-bg-hover text-brand border-l-2 border-brand"
                      : "text-ink-muted hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="col-span-12 md:col-span-9 lg:col-span-10 p-4 sm:p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
